import type { SearchActivitiesFilters } from '@funkey/ai-tools/schemas';
import type { Activity_Search_QueryParams } from '../shared';

/** A single active-filter chip rendered above the AI composer. */
export type AiFilterChip = {
    /** Stable key for the filter group (e.g. `'regions'`, `'budget'`). */
    id: string;
    /** Short text shown on the chip badge. */
    label: string;
    /** Localized phrase appended to the prompt textarea when the chip is added. */
    promptText: string;
};

/**
 * Minimal translate signature — mirrors `i18n._`. Kept narrow so the chip builder
 * stays a pure, easily-testable function (no full `I18n` instance / React context needed).
 */
export type ChipTranslate = (
    key: string,
    params?: Record<string, string | number>,
    opts?: { message?: string },
) => string;

/**
 * Minimal i18n shape. Calls go through `i18n._('literal', …)` (not an aliased `t(…)`) so
 * `lingui extract` actually sees the keys — otherwise they never land in the catalog and
 * the runtime `missing` handler renders them as `🚨<key>`. The real `I18n` instance is
 * assignable; tests pass a `{ _: stub }`.
 */
type ChipI18n = { _: ChipTranslate };

type BuildAiFilterChipsArgs = {
    i18n: ChipI18n;
    filters: Partial<Activity_Search_QueryParams>;
    /** Resolved display names keyed by id. */
    regionNameById: ReadonlyMap<number, string>;
    categoryNameById: ReadonlyMap<number, string>;
    languageNameById: ReadonlyMap<number, string>;
    /** City search context (from `CACHED_REGION_CITY_SEARCH`); null when none. */
    city: { address: string; radiusKm: number } | null;
};

const LOCATION_TYPE_KEYS: Record<string, { key: string; message: string }> = {
    INDOOR: { key: 'Indoor', message: 'Indoor' },
    OUTDOOR: { key: 'Outdoor', message: 'Outdoor' },
    VIRTUAL: { key: 'Online / Virtual', message: 'Online / Virtual' },
};

const SEASON_KEYS: Record<string, { key: string; message: string }> = {
    SPRING: { key: 'Spring', message: 'Spring' },
    SUMMER: { key: 'Summer', message: 'Summer' },
    FALL: { key: 'Fall', message: 'Fall' },
    WINTER: { key: 'Winter', message: 'Winter' },
};

const CLEANLINESS_KEYS: Record<string, { key: string; message: string }> = {
    CLEAN: { key: 'Clean', message: 'Clean' },
    WET: { key: 'Wet', message: 'Wet' },
    DIRTY: { key: 'Dirty', message: 'Dirty' },
};

const LOCATION_FILTER_KEYS: Record<string, { key: string; message: string }> = {
    // Same wording as the filter popup + drawer (region.activityFilters ROUTE_OPTIONS):
    // FIXED = "I go to the partner", CUSTOM = "Partner comes to me". The old
    // "Fixed location" / "Location of choice" labels were a different, wrong concept.
    FIXED: { key: 'I go to the partner', message: 'I go to the partner' },
    CUSTOM: { key: 'Partner comes to me', message: 'Partner comes to me' },
};

const DURATION_KEYS: Record<number, { key: string; message: string }> = {
    15: { key: '15 minutes', message: '15 minutes' },
    30: { key: 'Half an hour', message: 'Half an hour' },
    60: { key: '1 hour', message: '1 hour' },
    120: { key: '2 hours', message: '2 hours' },
    240: { key: 'Half a day', message: 'Half a day' },
    480: { key: 'Full day', message: 'Full day' },
};

const TRAIT_KEYS: Record<string, { key: string; message: string }> = {
    teamwork: { key: 'Teamwork', message: 'Teamwork' },
    action: { key: 'Action', message: 'Action' },
    competition: { key: 'Competition', message: 'Competition' },
    learning: { key: 'Learning', message: 'Learning' },
};

function namesFromIds(ids: readonly number[], nameById: ReadonlyMap<number, string>): string[] {
    return ids.map(id => nameById.get(id)).filter((name): name is string => !!name);
}

function joinEnum(
    values: readonly string[],
    map: Record<string, { key: string; message: string }>,
    i18n: ChipI18n,
): string {
    return values
        .map(value => map[value])
        .filter((entry): entry is { key: string; message: string } => !!entry)
        .map(entry => i18n._(entry.key, undefined, { message: entry.message }))
        .join(', ');
}

/**
 * Build the active-filter chips for the AI composer. Pure: given a filter snapshot and
 * resolved label maps it returns one chip per active filter group. Empty when nothing is
 * active. Mirrors `getActiveFilterCount`'s notion of "active" (non-empty, ignoring sort).
 */
export function buildAiFilterChips(args: BuildAiFilterChipsArgs): AiFilterChip[] {
    const { i18n, filters: f, regionNameById, categoryNameById, languageNameById, city } = args;
    const chips: AiFilterChip[] = [];

    // City + radius (resolves to regions, but carries its own readable phrase).
    if (city && city.address.length > 0) {
        chips.push({
            id: 'location',
            label: `${city.radiusKm} km · ${city.address}`,
            promptText: i18n._(
                'search.ai.chip.location',
                { radiusKm: city.radiusKm, address: city.address },
                { message: 'within {radiusKm} km of {address}' },
            ),
        });
    } else if (f.regions && f.regions.length > 0) {
        // Only show a plain regions chip when there's no city context driving them.
        const names = namesFromIds(f.regions, regionNameById);
        if (names.length > 0) {
            const joined = names.join(', ');
            chips.push({
                id: 'regions',
                label: joined,
                promptText: i18n._(
                    'search.ai.chip.regions',
                    { regions: joined },
                    { message: 'in {regions}' },
                ),
            });
        }
    }

    if (typeof f.participants === 'number') {
        chips.push({
            id: 'participants',
            label: i18n._(
                'search.ai.chip.participants.label',
                { count: f.participants },
                { message: '{count} people' },
            ),
            promptText: i18n._(
                'search.ai.chip.participants',
                { count: f.participants },
                { message: 'for {count} people' },
            ),
        });
    }

    if (typeof f.minBudget === 'number' || typeof f.maxBudget === 'number') {
        const range = `€${f.minBudget ?? 0} – €${f.maxBudget ?? '∞'}`;
        chips.push({
            id: 'budget',
            label: range,
            promptText: i18n._(
                'search.ai.chip.budget',
                { range },
                { message: 'with a budget of {range}' },
            ),
        });
    }

    const categoryNames = [
        ...namesFromIds(f.categories ?? [], categoryNameById),
        ...namesFromIds(f.subCategories ?? [], categoryNameById),
    ];
    if (categoryNames.length > 0) {
        const joined = categoryNames.join(', ');
        chips.push({
            id: 'categories',
            label: joined,
            promptText: i18n._(
                'search.ai.chip.categories',
                { categories: joined },
                { message: '{categories}' },
            ),
        });
    }

    if (f.languages && f.languages.length > 0) {
        const names = namesFromIds(f.languages, languageNameById);
        if (names.length > 0) {
            const joined = names.join(', ');
            chips.push({
                id: 'languages',
                label: joined,
                promptText: i18n._(
                    'search.ai.chip.languages',
                    { languages: joined },
                    { message: 'in {languages}' },
                ),
            });
        }
    }

    if (f.locationType && f.locationType.length > 0) {
        const joined = joinEnum(f.locationType, LOCATION_TYPE_KEYS, i18n);
        if (joined.length > 0)
            chips.push({ id: 'locationType', label: joined, promptText: joined });
    }

    if (f.location && f.location.length > 0) {
        const joined = joinEnum(f.location, LOCATION_FILTER_KEYS, i18n);
        if (joined.length > 0)
            chips.push({ id: 'location-type', label: joined, promptText: joined });
    }

    if (f.season && f.season.length > 0) {
        const joined = joinEnum(f.season, SEASON_KEYS, i18n);
        if (joined.length > 0) {
            chips.push({
                id: 'season',
                label: joined,
                promptText: i18n._(
                    'search.ai.chip.season',
                    { seasons: joined },
                    { message: 'in {seasons}' },
                ),
            });
        }
    }

    if (f.cleanliness && f.cleanliness.length > 0) {
        const joined = joinEnum(f.cleanliness, CLEANLINESS_KEYS, i18n);
        if (joined.length > 0) chips.push({ id: 'cleanliness', label: joined, promptText: joined });
    }

    if (typeof f.minDuration === 'number' || typeof f.maxDuration === 'number') {
        const minLabel =
            typeof f.minDuration === 'number' && DURATION_KEYS[f.minDuration]
                ? i18n._(DURATION_KEYS[f.minDuration]!.key, undefined, {
                      message: DURATION_KEYS[f.minDuration]!.message,
                  })
                : i18n._('No min.', undefined, { message: 'No min.' });
        const maxLabel =
            typeof f.maxDuration === 'number' && DURATION_KEYS[f.maxDuration]
                ? i18n._(DURATION_KEYS[f.maxDuration]!.key, undefined, {
                      message: DURATION_KEYS[f.maxDuration]!.message,
                  })
                : i18n._('No max.', undefined, { message: 'No max.' });
        const range = `${minLabel} – ${maxLabel}`;
        chips.push({
            id: 'duration',
            label: range,
            promptText: i18n._(
                'search.ai.chip.duration',
                { duration: range },
                { message: 'lasting {duration}' },
            ),
        });
    }

    const traitLabels = (['teamwork', 'action', 'competition', 'learning'] as const)
        .filter(name => {
            const value = f[name];
            return Array.isArray(value) && value.length > 0;
        })
        .map(name =>
            i18n._(TRAIT_KEYS[name]!.key, undefined, { message: TRAIT_KEYS[name]!.message }),
        );
    if (traitLabels.length > 0) {
        const joined = traitLabels.join(', ');
        chips.push({ id: 'traits', label: joined, promptText: joined });
    }

    return chips;
}

/** Maps a composer/result filter chip id to the drawer section it opens/edits. */
export const DRAWER_SECTION_BY_CHIP_ID: Record<string, string> = {
    participants: 'participants',
    budget: 'budget',
    location: 'regions',
    regions: 'regions',
    categories: 'categories',
    languages: 'language',
    locationType: 'location',
    'location-type': 'location',
    season: 'season',
    duration: 'duration',
    cleanliness: 'traits',
    traits: 'traits',
};

/** A chip shown inside a search-result message for a filter the AI actually applied. */
type AiResultFilterChip = {
    id: string;
    /** Chip text — matches the summary tail (same i18n keys as `buildSummaryLabel`). */
    label: string;
    /** Drawer section id to expand when the chip is clicked. */
    section: string;
};

/**
 * Build the clickable filter chips for an assistant search result. Surfaces the real
 * filters the AI applied (participants, budget, location) — never `query`. Labels reuse
 * the same i18n keys as the summary tail so chip text and summary stay in sync. Clicking a
 * chip opens the filter drawer at the mapped `section`.
 */
export function buildResultFilterChips(
    i18n: ChipI18n,
    filters: SearchActivitiesFilters,
): AiResultFilterChip[] {
    const chips: AiResultFilterChip[] = [];

    if (filters.participants !== undefined) {
        chips.push({
            id: 'participants',
            section: 'participants',
            label: i18n._(
                'activity.search.ai.summary.partySize',
                { count: filters.participants },
                { message: '{count} pers.' },
            ),
        });
    }

    if (filters.locationLabel) {
        chips.push({ id: 'location', section: 'regions', label: filters.locationLabel });
    }

    if (filters.minBudget !== undefined && filters.maxBudget !== undefined) {
        chips.push({
            id: 'budget',
            section: 'budget',
            label: i18n._(
                'activity.search.ai.summary.budgetRange',
                { min: filters.minBudget, max: filters.maxBudget },
                { message: '€{min}–€{max} p.p.' },
            ),
        });
    } else if (filters.maxBudget !== undefined) {
        chips.push({
            id: 'budget',
            section: 'budget',
            label: i18n._(
                'activity.search.ai.summary.maxBudget',
                { budget: filters.maxBudget },
                { message: '≤ €{budget} p.p.' },
            ),
        });
    } else if (filters.minBudget !== undefined) {
        chips.push({
            id: 'budget',
            section: 'budget',
            label: i18n._(
                'activity.search.ai.summary.minBudget',
                { budget: filters.minBudget },
                { message: '≥ €{budget} p.p.' },
            ),
        });
    }

    if (filters.locationType?.length) {
        const joined = joinEnum(filters.locationType, LOCATION_TYPE_KEYS, i18n);
        if (joined.length > 0)
            chips.push({ id: 'locationType', section: 'location', label: joined });
    }

    if (filters.season?.length) {
        const joined = joinEnum(filters.season, SEASON_KEYS, i18n);
        if (joined.length > 0) chips.push({ id: 'season', section: 'season', label: joined });
    }

    if (filters.cleanliness?.length) {
        const joined = joinEnum(filters.cleanliness, CLEANLINESS_KEYS, i18n);
        if (joined.length > 0) chips.push({ id: 'cleanliness', section: 'traits', label: joined });
    }

    if (filters.categories?.length) {
        chips.push({
            id: 'categories',
            section: 'categories',
            label: filters.categories.join(', '),
        });
    }

    if (filters.traits?.length) {
        const joined = joinEnum(filters.traits, TRAIT_KEYS, i18n);
        if (joined.length > 0) chips.push({ id: 'traits', section: 'traits', label: joined });
    }

    if (filters.duration) {
        const label = {
            short: i18n._('search.ai.summary.duration.short', undefined, { message: 'short' }),
            halfDay: i18n._('search.ai.summary.duration.halfDay', undefined, {
                message: 'half day',
            }),
            fullDay: i18n._('search.ai.summary.duration.fullDay', undefined, {
                message: 'full day',
            }),
        }[filters.duration];
        chips.push({ id: 'duration', section: 'duration', label });
    }

    return chips;
}
